import React from "react";
import closeIcon from "@surgia/ui/icons/popup/close.svg";
import hideIcon from "@surgia/ui/icons/popup/hide.svg";
import folderIcon from "@surgia/ui/icons/popup/folder.svg";
import Button from "@surgia/ui/components/button";
import { useTranslation } from "react-i18next";

export interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  icon?: "hide" | "folder" | "custom";
  customIcon?: string;
  primaryAction: {
    label: string;
    onClick: () => void;
    variant?: "primary" | "danger" | "purple";
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  content?: React.ReactNode;
  showCloseButton?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  title,
  description,
  icon = "folder",
  customIcon,
  primaryAction,
  secondaryAction,
  content,
  showCloseButton = true,
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  if (!open) return null;

  const getIcon = () => {
    if (customIcon) return customIcon;
    switch (icon) {
      case "hide":
        return hideIcon;
      case "folder":
        return folderIcon;
      default:
        return folderIcon;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
      <div
        className="bg-white rounded-xl shadow-xl relative flex flex-col items-center"
        style={{
          width: "400px",
          maxWidth: "400px",
          minHeight: "300px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow:
            "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)",
        }}
      >
        {/* Close Button */}
        {showCloseButton && (
          <img
            src={closeIcon}
            alt="close"
            onClick={onClose}
            className={`absolute top-4 ${
              isRTL ? "left-4" : "right-4"
            } text-gray-400 hover:text-gray-600 cursor-pointer w-6 h-6`}
          />
        )}

        {/* Icon */}
        <div className="mt-6 mb-4">
          <img src={getIcon()} alt="modal icon" className="w-12 h-12" />
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2 px-6 text-center">
          {title}
        </h2>

        {/* Description */}
        <p className="text-sm text-gray-500 mb-4 px-6 text-center">
          {description}
        </p>

        {/* Custom Content */}
        {content && <div className="w-full px-6 mb-4">{content}</div>}

        {/* Action Buttons */}
        <div className="flex gap-3 w-full px-6 mb-6 mt-auto">
          {secondaryAction && (
            <Button
              variant="cancel"
              onClick={secondaryAction.onClick}
              className="flex-1"
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button
            variant={primaryAction.variant || "primary"}
            onClick={primaryAction.onClick}
            className="flex-1"
          >
            {primaryAction.label}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
